"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Bot, Sparkles } from "lucide-react";

/**
 * 3D-Hologramm-Board im Hintergrund + gestochen scharfe, gerade ausgerichtete
 * Workflow-Nodes im Vordergrund.
 */
export default function IdeaScratcherPreview() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const durations = [900, 900, 900, 2600];
    const timer = setTimeout(() => {
      setStep((prev) => (prev + 1) % 4);
    }, durations[step]);
    return () => clearTimeout(timer);
  }, [step]);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-3 select-none"
      style={{ perspective: "900px" }}
    >
      {/* 1. Volumetrisches Nebula-Licht */}
      <motion.div
        animate={{
          scale: [1, 1.25, 1],
          opacity: step >= 2 ? [0.6, 0.9, 0.6] : 0.4,
        }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-amber-500/20 via-purple-500/30 to-emerald-500/25 blur-3xl"
      />

      {/* 2. Gekipptes 3D-Bodenraster (nur der Hintergrund kippt!) */}
      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* 3. Gestochen scharfe Nodes & SVG-Signale im Vordergrund */}
      <div className="relative z-10 h-full max-h-[164px] w-full max-w-[305px] antialiased">
        <svg
          viewBox="0 0 305 164"
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        >
          <motion.path
            d="M 86 42 C 122 42, 118 82, 148 82"
            fill="none"
            stroke="#a855f7"
            strokeWidth="2.5"
            strokeDasharray="5 4"
            animate={{
              pathLength: step >= 1 ? 1 : 0,
              opacity: step >= 1 ? 0.85 : 0,
            }}
            transition={{ duration: 0.45 }}
          />
          <motion.path
            d="M 205 82 C 230 82, 215 124, 236 124"
            fill="none"
            stroke="#10b981"
            strokeWidth="2.5"
            strokeDasharray="5 4"
            animate={{
              pathLength: step >= 2 ? 1 : 0,
              opacity: step >= 2 ? 0.9 : 0,
            }}
            transition={{ duration: 0.45 }}
          />

          {step === 3 && (
            <>
              <motion.circle
                r="4.5"
                fill="#f59e0b"
                animate={{
                  cx: [86, 118, 148],
                  cy: [42, 62, 82],
                  opacity: [0, 1, 0],
                }}
                transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.circle
                r="4.5"
                fill="#10b981"
                animate={{
                  cx: [205, 220, 236],
                  cy: [82, 103, 124],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 1,
                  delay: 0.35,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </>
          )}
        </svg>

        {/* Node 1: Idee */}
        <motion.div
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{ left: "10px", top: "18px" }}
          className="absolute flex items-center gap-1.5 rounded-xl border border-amber-400/80 bg-white px-3 py-1.5 shadow-[0_12px_24px_-4px_rgba(245,158,11,0.45)] dark:bg-zinc-900"
        >
          <Lightbulb className="h-3.5 w-3.5 text-amber-500" />
          <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
            Idee
          </span>
          <span className="ml-0.5 h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_6px_#f59e0b]" />
        </motion.div>

        {/* Node 2: Agent */}
        <motion.div
          animate={{
            opacity: step >= 1 ? 1 : 0,
            y: step >= 1 ? 0 : 8,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          style={{ left: "110px", top: "62px" }}
          className="absolute flex items-center gap-1.5 rounded-xl border border-purple-400/80 bg-white px-3 py-1.5 shadow-[0_14px_28px_-4px_rgba(168,85,247,0.55)] dark:bg-zinc-900"
        >
          <Bot className="h-3.5 w-3.5 text-purple-500" />
          <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
            Agent
          </span>
          <span className="ml-0.5 h-2 w-2 rounded-full bg-purple-500 shadow-[0_0_6px_#a855f7]" />
        </motion.div>

        {/* Node 3: Live! */}
        <motion.div
          animate={{
            opacity: step >= 2 ? 1 : 0,
            y: step >= 2 ? 0 : 8,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
          style={{ right: "8px", bottom: "14px" }}
          className="absolute flex items-center gap-1.5 rounded-xl border border-emerald-400/80 bg-white px-3 py-1.5 shadow-[0_16px_32px_-4px_rgba(16,185,129,0.6)] dark:bg-zinc-900"
        >
          <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
          <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
            Live!
          </span>
        </motion.div>
      </div>
    </div>
  );
}
